import { type PublicKeyCredentialCreationOptionsJSON, startRegistration } from "@simplewebauthn/browser";
import QRCode from "qrcode";
import { useEffect, useRef, useState } from "react";
import type { Root } from "../../../../../supabase/functions/sync-control/model";
import { interfaceLocale, useTranslation } from "../../i18n";
import { createEncryptedBackup, decryptBackupInWorker } from "../localStore/backupJob";
import { supabase } from "../supabase";
import { pairQr, readPairQr } from "./pairing";
import { type PairCandidate, PairSession, recoverDevice } from "./pairSession";
import { syncMessage } from "./syncMessage";
import type { useSyncReview, VerifyOperation } from "./useSyncReview";

type Controller = ReturnType<typeof useSyncReview>;
export function DeviceJoining({
  controller,
  workspaceId,
  verify,
  onReauthenticate,
}: {
  controller: Controller;
  workspaceId: string;
  verify: VerifyOperation;
  onReauthenticate: () => Promise<void>;
}) {
  const { t } = useTranslation("syncReview");
  const [busy, setBusy] = useState(false);
  const [resetVerification, setResetVerification] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [newTotp, setNewTotp] = useState<{ id: string; qr: string } | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const enrollment = useRef<{ challengeId: string; options: PublicKeyCredentialCreationOptionsJSON } | null>(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const session = useRef<PairSession | null>(null);
  const [qr, setQr] = useState("");
  const [candidate, setCandidate] = useState<PairCandidate | null>(null);
  const [scanning, setScanning] = useState(false);
  const [url, setUrl] = useState("");
  const [remember, setRemember] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [joining, setJoining] = useState<{ root: Root; material: string } | null>(null);
  const [localData, setLocalData] = useState(false);
  const [backup, setBackup] = useState("");
  const [backupPassword, setBackupPassword] = useState("");
  const [backupFile, setBackupFile] = useState<File | null>(null);
  const [receipt, setReceipt] = useState<string | undefined>();
  const [reenroll, setReenroll] = useState("");
  const [expires, setExpires] = useState(0);
  const active = useRef(true);
  const polling = useRef(false);
  const authorized =
    !!controller.device.current &&
    controller.device.current.scope.datasetId === controller.status.summary?.root?.datasetId &&
    controller.device.current.scope.generation === controller.status.summary?.root?.generation;
  const perform = async (task: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await task();
    } catch (e) {
      if (active.current) setError(t(syncMessage(e instanceof Error ? e.message : "")));
    } finally {
      if (active.current) setBusy(false);
    }
  };
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      session.current = null;
    };
  }, []);
  const ready = async (value: { root: Root; material: string }) => {
    const source = await controller.client!.exportBackupSource(workspaceId);
    setLocalData(!!(source.snapshot.records.length || source.snapshot.syncArchives?.length));
    setJoining(value);
    setReceipt(undefined);
    setQr("");
    setCandidate(null);
  };
  const scan = async (value: string) => {
    const ticket = readPairQr(value, location.origin);
    setUrl("");
    const pair = PairSession.join(controller.api.current!, ticket);
    session.current = pair;
    setExpires(ticket.expiresAt);
    if (!authorized) {
      await pair.requestJoin();
      setMessage(t("waitApproval"));
    } else {
      setCandidate(await pair.candidate());
      setMessage(t("checkDevice"));
    }
  };
  useEffect(() => {
    if (!controller.api.current || !location.hash) return;
    const value = location.href;
    history.replaceState(null, "", `${location.pathname}${location.search}`);
    void perform(() => scan(value));
  }, [controller.status.ready]);
  useEffect(() => {
    const timer = setInterval(() => {
      const pair = session.current;
      if (!pair || polling.current || joining || busy) return;
      if (pair.ticket.expiresAt <= Date.now()) {
        session.current = null;
        setQr("");
        setCandidate(null);
        setError(t("pairExpired"));
        return;
      }
      polling.current = true;
      void (async () => {
        try {
          if (authorized) {
            const person = await pair.candidate();
            if (person) setCandidate(person);
          } else {
            const result = await pair.approval();
            if (result) await ready(result);
          }
        } catch (e) {
          if (e instanceof Error && e.message !== "sync-pair-expired") setError(t(syncMessage(e.message)));
        } finally {
          polling.current = false;
        }
      })();
    }, 2000);
    return () => clearInterval(timer);
  }, [authorized, joining, busy, controller.client, workspaceId, t]);
  const create = () =>
    perform(async () => {
      const pair = await PairSession.create(controller.api.current!, authorized);
      session.current = pair;
      setCandidate(null);
      setExpires(pair.ticket.expiresAt);
      setQr(
        await QRCode.toDataURL(pairQr(pair.ticket, location.origin), {
          errorCorrectionLevel: "M",
          scale: 6,
          margin: 4,
        }),
      );
      if (!authorized) await pair.requestJoin();
      setMessage(t("pairScanHint"));
    });
  const recover = () =>
    perform(async () => {
      try {
        if (!file || file.size > 32768) throw new Error("sync-recovery-format");
        const result = await recoverDevice(controller.api.current!, await file.text(), password, resetVerification);
        if (result.reauthenticate) {
          setNeedsReauth(true);
          setJoining(null);
          setReenroll("");
          return;
        }
        setReenroll(result.reenrollProof);
        await ready(result);
      } finally {
        setPassword("");
      }
    });
  const saveBackup = () =>
    perform(async () => {
      const value = await createEncryptedBackup(
        () => controller.client!.exportBackupSource(workspaceId),
        backupPassword,
        controller.api.current!.device.deviceId,
        new AbortController().signal,
        () => undefined,
      );
      setBackup(value);
      download(value, `classroom-before-sync-${Date.now()}.rollclassbackup`);
      setReceipt(undefined);
    });
  const checkBackup = () =>
    perform(async () => {
      if (!backupFile || !backup) throw new Error("sync-backup-current-required");
      const contents = await backupFile.text();
      if (contents !== backup) throw new Error("sync-backup-current-required");
      const restored = await decryptBackupInWorker(contents, backupPassword);
      const token = await controller.client!.syncVerifyJoinBackup(workspaceId, restored.snapshot);
      setReceipt(token);
      setBackupPassword("");
    });
  const finish = () =>
    perform(async () => {
      if (!joining) throw new Error("sync-setup-unavailable");
      const failure = await controller.join(joining.root, joining.material, remember, receipt);
      const pair = session.current;
      session.current = null;
      setJoining(null);
      setBackup("");
      setFile(null);
      setMessage(t(failure ? "pairInterrupted" : "pairFinishing"));
      await pair?.complete();
      setMessage(t(failure ? "pairInterrupted" : "pairConnected"));
    });
  const register = () =>
    perform(async () => {
      const settings =
        enrollment.current ??
        (await controller.api.current!.request<{
          challengeId: string;
          options: PublicKeyCredentialCreationOptionsJSON;
        }>("verification-options", { proofId: reenroll }));
      enrollment.current = settings;
      const response = await startRegistration({ optionsJSON: settings.options });
      await controller.api.current!.request("verification-verify", { challengeId: settings.challengeId, response });
      setReenroll("");
      enrollment.current = null;
      setMessage(t("enrolled"));
    });
  const beginRecoveryTotp = () =>
    perform(async () => {
      const { data, error } = await supabase!.auth.mfa.enroll({ factorType: "totp", friendlyName: "課序同步復原" });
      if (error || !data) throw new Error("sync-verification-reset-required");
      setNewTotp({
        id: data.id,
        qr: data.totp.qr_code.startsWith("data:")
          ? data.totp.qr_code
          : `data:image/svg+xml;charset=utf-8,${encodeURIComponent(data.totp.qr_code)}`,
      });
    });
  const finishRecoveryTotp = () =>
    perform(async () => {
      try {
        if (!newTotp) throw new Error("sync-verification-failed");
        const { data, error } = await supabase!.auth.mfa.challenge({ factorId: newTotp.id });
        if (error || !data) throw new Error("sync-verification-failed");
        await controller.api.current!.request("verification-totp", {
          proofId: reenroll,
          factorId: newTotp.id,
          challengeId: data.id,
          code: totpCode,
        });
        setReenroll("");
        setNewTotp(null);
        setMessage(t("enrolled"));
      } finally {
        setTotpCode("");
      }
    });
  return (
    <section className="sync-device-joining">
      <h2>{t("deviceManagement")}</h2>
      {needsReauth && (
        <>
          <p role="status">{t("verificationResetReauth")}</p>
          <button type="button" disabled={busy} onClick={() => void onReauthenticate()}>
            {t("reauthenticateRecovery")}
          </button>
        </>
      )}
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      {!joining && !needsReauth && (
        <>
          <button type="button" disabled={busy || !controller.configured} onClick={create}>
            {t("showPairQr")}
          </button>
          <button type="button" disabled={busy || !controller.configured} onClick={() => setScanning(true)}>
            {t("scanPairQr")}
          </button>
          {qr && (
            <>
              <img src={qr} width={320} height={320} style={{ maxWidth: "100%", height: "auto" }} alt={t("pair")} />
              <p>{t("pairExpiry", { time: new Date(expires).toLocaleTimeString(interfaceLocale()) })}</p>
            </>
          )}
          {scanning && (
            <QrCamera
              onResult={(value) => {
                setScanning(false);
                void perform(() => scan(value));
              }}
              onClose={() => setScanning(false)}
            />
          )}
          <details>
            <summary>{t("pastePairUrl")}</summary>
            <label>
              {t("pairUrl")}
              <input type="password" autoComplete="off" value={url} onChange={(e) => setUrl(e.target.value)} />
            </label>
            <button type="button" disabled={busy || !url} onClick={() => void perform(() => scan(url))}>
              {t("readPairUrl")}
            </button>
          </details>
          {candidate && authorized && (
            <div>
              <p>{t("approveDevice", { name: candidate.label })}</p>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void perform(async () => {
                    await session.current!.approve(controller.device.current!);
                    session.current = null;
                    setCandidate(null);
                    setQr("");
                    setMessage(t("pairApproved"));
                  })
                }
              >
                {t("approvePair")}
              </button>
            </div>
          )}
          {!authorized && (
            <details>
              <summary>{t("recover")}</summary>
              <label>
                <input
                  type="checkbox"
                  checked={resetVerification}
                  disabled={busy}
                  onChange={(e) => setResetVerification(e.target.checked)}
                />
                {t("resetLostVerification")}
              </label>
              <p>{t("resetVerificationHint")}</p>
              <label>
                {t("recoveryFileName")}
                <input
                  type="file"
                  accept=".classroomsyncrecovery"
                  disabled={busy}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </label>
              <label>
                {t("openPassword")}
                <input
                  type="password"
                  autoComplete="current-password"
                  disabled={busy}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              <button type="button" disabled={busy || !file || !password} onClick={recover}>
                {t("recover")}
              </button>
            </details>
          )}
        </>
      )}
      {joining && (
        <>
          <label>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={busy} />
            {t("privateDevice")}
          </label>
          {localData && (
            <>
              <p>{t("existingLocalBarrier")}</p>
              <label>
                {t("backupPassword")}
                <input
                  type="password"
                  autoComplete="new-password"
                  value={backupPassword}
                  onChange={(e) => setBackupPassword(e.target.value)}
                  disabled={busy}
                />
              </label>
              <button type="button" onClick={saveBackup} disabled={busy || [...backupPassword].length < 15}>
                {t("downloadJoinBackup")}
              </button>
              {backup && (
                <>
                  <label>
                    {t("reselectJoinBackup")}
                    <input
                      type="file"
                      accept=".rollclassbackup"
                      onChange={(e) => {
                        setBackupFile(e.target.files?.[0] ?? null);
                        setReceipt(undefined);
                      }}
                    />
                  </label>
                  <button type="button" onClick={checkBackup} disabled={busy || !backupFile || !backupPassword}>
                    {t("verifyFile")}
                  </button>
                  {receipt && <p>{t("backupVerified")}</p>}
                </>
              )}
            </>
          )}
          {reenroll && (
            <>
              <button type="button" onClick={register} disabled={busy}>
                {t("reenrollVerification")}
              </button>
              <button type="button" onClick={beginRecoveryTotp} disabled={busy}>
                {t("enrollTotp")}
              </button>
              {newTotp && (
                <>
                  <img src={newTotp.qr} width={180} height={180} alt={t("totp")} />
                  <label>
                    {t("totpCode")}
                    <input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={totpCode}
                      maxLength={6}
                      onChange={(e) => setTotpCode(e.target.value)}
                    />
                  </label>
                  <button type="button" onClick={finishRecoveryTotp} disabled={busy || !/^\d{6}$/.test(totpCode)}>
                    {t("confirmTotp")}
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setJoining(null);
                  setReenroll("");
                  enrollment.current = null;
                  setNewTotp(null);
                  setResetVerification(true);
                }}
              >
                {t("restartWithReset")}
              </button>
            </>
          )}
          <button type="button" onClick={finish} disabled={busy || !!reenroll || (localData && !receipt)}>
            {t(localData ? "replaceFromCloud" : "joinCloud")}
          </button>
          {!reenroll && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void perform(async () => {
                  if (!joining) return;
                  await controller.adoptIdentity(joining.root, joining.material, remember);
                  if (session.current) await session.current.complete();
                  session.current = null;
                  setJoining(null);
                  setMessage(t("keepLocalNext"));
                })
              }
            >
              {t("keepLocalGeneration")}
            </button>
          )}
        </>
      )}
    </section>
  );
}
function download(contents: string, name: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: "application/octet-stream" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
export function QrCamera({ onResult, onClose }: { onResult: (value: string) => void; onClose: () => void }) {
  const { t } = useTranslation("syncReview");
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState(false);
  const result = useRef(onResult);
  result.current = onResult;
  useEffect(() => {
    let cancelled = false;
    let stop: (() => void) | undefined;
    void import("@zxing/browser")
      .then(async ({ BrowserQRCodeReader }) => {
        if (cancelled || !video.current) return;
        const controls = await new BrowserQRCodeReader().decodeFromConstraints(
          { video: { facingMode: { ideal: "environment" } }, audio: false },
          video.current,
          (decoded, _error, handle) => {
            if (decoded && !cancelled) {
              handle.stop();
              cancelled = true;
              result.current(decoded.getText());
            }
          },
        );
        stop = () => controls.stop();
        if (cancelled) stop();
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
      stop?.();
      if (video.current?.srcObject instanceof MediaStream)
        for (const track of video.current.srcObject.getTracks()) track.stop();
    };
  }, []);
  return (
    <div className="sync-pair-camera">
      <h3>{t("pairCameraTitle")}</h3>
      <p>{t("pairCameraHint")}</p>
      <div className="sync-pair-camera-frame">
        <video ref={video} playsInline muted aria-label={t("scanPairQr")} />
        <span className="sync-pair-camera-target" aria-hidden="true" />
      </div>
      {!error && <p className="sync-quiet">{t("pairCameraPermission")}</p>}
      {error && <p role="alert">{t("cameraUnavailable")}</p>}
      <button type="button" onClick={onClose}>
        {t("pairBackToOptions")}
      </button>
    </div>
  );
}
